import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X, Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function AllowedDomains() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [domains, setDomains] = useState([]);
  const [add, setAdd] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings/allowed-domains").then(({ data }) => setDomains(data.domains || [])); }, []);

  const persist = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/allowed-domains", { domains: next });
      setDomains(data.domains);
      toast.success("Allowed domains updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const addDomain = () => {
    const d = add.trim().toLowerCase().replace(/^@/, "");
    if (!d || !/^[a-z0-9.-]+\.[a-z]{2,}$/.test(d)) { toast.error("Enter a valid domain like acehcs.com"); return; }
    if (domains.includes(d)) { toast.error("Already added"); return; }
    setAdd("");
    persist([...domains, d]);
  };
  const removeDomain = (d) => {
    if (domains.length <= 1) { toast.error("At least one domain is required"); return; }
    persist(domains.filter((x) => x !== d));
  };

  return (
    <Card className="p-6 border-slate-200 shadow-none" data-testid="allowed-domains-card">
      <div className="mb-4">
        <div className="text-xs uppercase tracking-widest font-mono-plex text-slate-500">Access control</div>
        <h3 className="font-heading font-bold text-xl">Corporate email domains</h3>
        <p className="text-sm text-slate-500 mt-1">Only users signing in with these email domains can join the workspace (JWT register and Google OAuth new signups).</p>
      </div>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="domains-list">
        {domains.length === 0 && <span className="text-sm text-slate-400">No domains configured yet.</span>}
        {domains.map((d) => (
          <Badge key={d} variant="outline" className="text-sm px-3 py-1 border-slate-300" data-testid={`domain-${d}`}>
            @{d}
            {isAdmin && (
              <button onClick={() => removeDomain(d)} className="ml-2 text-slate-400 hover:text-rose-600 transition-colors duration-200" data-testid={`domain-remove-${d}`}>
                <X className="w-3 h-3 inline" />
              </button>
            )}
          </Badge>
        ))}
      </div>
      {isAdmin && (
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">@</span>
            <Input value={add} onChange={(e) => setAdd(e.target.value)} placeholder="yourcompany.com" className="pl-7" data-testid="domain-add-input" onKeyDown={(e) => e.key === "Enter" && addDomain()} />
          </div>
          <Button onClick={addDomain} disabled={saving} data-testid="domain-add-btn" className="bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4 mr-1" /> Add</Button>
        </div>
      )}
    </Card>
  );
}
